import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import { useSetTrue, useToggle } from '../../../hooks'
import { track } from '../../../scripts/segment'
import Form from '../Form'
import Announce from '../../Announce/Announce'
import { debounce } from '../../../utils/'

const EmailSignUp = ({
  messages = {},
  label = 'Email (required)',
  instruction = 'The Subscribe button will be enabled once you enter a valid email address and agree to the terms.',
  message = 'Please enter a valid email',
  buttonText = 'Subscribe',
  submitDetail = {},
  trackDetail = {},
  onSubmitSuccess = () => {},
  showFormOnSuccess = false,
  inputAccentColor,
  checkboxAccentColor,
  buttonBgColor = '',
  buttonTextColor = '',
  buttonCustomStateColors = {},
  trackEventName = 'Web Form Submitted',
  isInActionDetail = true,
}) => {
  const [value, setValue] = useState('')
  const [isError, setIsError] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [buttonAvailable, setButtonAvailable] = useState(false)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const { isOpen: isChecked, toggle: setIsChecked } = useToggle()
  const { isTrue: isSubmitted, setTrue: setIsSubmitted } = useSetTrue()
  const inputRef = useRef(null)
  const errorId = `email-error-${Math.random().toString(36).slice(2, 11)}`

  const isValidEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email)
  }

  const isButtonDisabled = () => {
    if (value === '') {
      return true
    } else {
      if (isChecked) {
        return false
      }
      return true
    }
  }

  const onInputChange = (e) => {
    setValue(e.currentTarget.value)
    if (submitAttempted) {
      setSubmitAttempted(false)
    }
  }

  useEffect(() => {
    if (value === '') {
      setIsError(null)
      setErrorMsg('')
    } else {
      const emailIsValid = isValidEmail(value)
      setIsError(!emailIsValid)

      if (emailIsValid) {
        setErrorMsg('')
      }
    }
  }, [value, isButtonDisabled])

  useEffect(() => {
    const noButtonStatus = isButtonDisabled()

    let timeoutId

    if (noButtonStatus) {
      setButtonAvailable(false)
    } else {
      const debounceSetButton = debounce(() => setButtonAvailable(true), 100)
      debounceSetButton()
    }
  }, [value, isChecked, isError])

  const onSubmit = (e) => {
    e.preventDefault()
    setSubmitAttempted(true)

    const input = inputRef.current
    const currentValue = input && input.value

    if (isError || !currentValue || !isChecked) {
      if (input) {
        input.focus()
      }

      if (!currentValue || isError || !isChecked) {
        setErrorMsg(message)
      }
    } else {
      setErrorMsg('')
      setValue('')
      setIsChecked()
      setIsSubmitted()
      onSubmitSuccess()

      const submitInfo = {
        email: currentValue,
        newsletters: 1,
        ...submitDetail,
      }

      const trackInfo = {
        textOfButtonOrLink: buttonText,
        email: currentValue,
        ...trackDetail,
      }

      if (window.analytics) {
        window.analytics.identify(analytics.user().id(), submitInfo)
        track(trackEventName, trackInfo, isInActionDetail)
      }
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !isButtonDisabled()) {
      onSubmit(e)
    }
  }

  const LegalLabel = messages.LegalCopy
  const Success = messages.Success
  const Heading = messages.Heading

  if (isSubmitted && !showFormOnSuccess && messages.Success) {
    return <Success />
  }

  return (
    <>
      {isSubmitted && showFormOnSuccess && messages.Success && <Success />}
      {messages.Heading && <Heading />}
      <Announce id="signup-instruction" message={instruction} />
      <Form className="newsletter-form" aria-describedby="signup-instruction">
        <Form.Input
          ref={inputRef}
          type="email"
          label={label}
          value={value}
          onChange={onInputChange}
          errorMsg={errorMsg}
          isError={isError && errorMsg !== ''}
          onKeyDown={onKeyDown}
          accentColor={inputAccentColor}
          aria-describedby={errorMsg ? errorId : undefined}
        />
        {buttonAvailable && !errorMsg && (
          <Announce
            id="button-available"
            role="alert"
            message="The Subscribe button is now available. You can click it to submit your email."
          />
        )}
        {errorMsg && <Announce id={errorId} role="alert" message={errorMsg} />}

        <Form.Checkbox
          isChecked={isChecked}
          onChange={setIsChecked}
          accentColor={checkboxAccentColor}
        >
          {messages.LegalCopy && <LegalLabel />}
        </Form.Checkbox>

        <Form.Button
          tabIndex="0"
          text={buttonText}
          onClick={onSubmit}
          isDisabled={isButtonDisabled()}
          bgColor={buttonBgColor}
          textColor={buttonTextColor}
          customStateColors={buttonCustomStateColors}
        />
      </Form>
    </>
  )
}

EmailSignUp.propTypes = {
  messages: PropTypes.object,
  label: PropTypes.string,
  message: PropTypes.string,
  buttonText: PropTypes.string,
  submitDetail: PropTypes.object,
  trackDetail: PropTypes.object,
  onSubmitSuccess: PropTypes.func,
  showFormOnSuccess: PropTypes.bool,
  inputAccentColor: PropTypes.string,
  checkboxAccentColor: PropTypes.string,
  buttonBgColor: PropTypes.string,
  buttonTextColor: PropTypes.string,
  buttonCustomStateColors: PropTypes.object,
  trackEventName: PropTypes.string,
  isInActionDetail: PropTypes.bool,
}

export default EmailSignUp
